import React, { useEffect, useState } from "react";
import { Modal, Button } from "react-bootstrap";
import { Download, ExternalLink } from "react-feather";

/**
 * In-page preview for a fetched attachment blob - PDFs and images alike.
 *
 * Replaces the `window.open(URL.createObjectURL(blob))` pattern used across this app
 * (petty cash receipts, employee documents, enquiry and quotation attachments), which
 * throws the user into a bare browser tab and loses the context they were working in.
 *
 * Type is taken from the blob's own Content-Type where the server sent one, falling back
 * to the file extension - a blob with a missing or generic type would otherwise render as
 * "can't preview" even though it is a perfectly good PDF.
 *
 * The object URL is created when the modal opens and revoked when it closes, so nothing
 * leaks and nothing is revoked while the iframe is still reading it.
 */
const AttachmentPreviewModal = ({ show, onClose, blob, fileName, title }) => {
  const [objectUrl, setObjectUrl] = useState(null);

  useEffect(() => {
    if (!show || !blob) {
      setObjectUrl(null);
      return undefined;
    }
    const url = URL.createObjectURL(blob);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [show, blob]);

  const name = fileName || "attachment";
  const ext = name.split(".").pop()?.toLowerCase() || "";
  const type = (blob?.type || "").toLowerCase();

  const isPdf = type.includes("pdf") || ext === "pdf";
  const isImage = type.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp", "bmp"].includes(ext);

  const download = () => {
    if (!objectUrl) return;
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = name;
    a.click();
  };

  return (
    <Modal show={show} onHide={onClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title className="h6">
          {title || "Attachment"}
          <span className="text-muted ms-2 fw-normal">{name}</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ background: "#e9ecef", minHeight: "70vh", padding: 0 }}>
        {!objectUrl && <div className="p-4 text-muted">Loading…</div>}

        {objectUrl && isPdf && (
          <iframe
            title={name}
            src={objectUrl}
            style={{ width: "100%", height: "78vh", border: 0, display: "block" }}
          />
        )}

        {objectUrl && isImage && (
          <div className="d-flex justify-content-center align-items-center p-3" style={{ minHeight: "70vh" }}>
            <img src={objectUrl} alt={name} style={{ maxWidth: "100%", maxHeight: "74vh", objectFit: "contain" }} />
          </div>
        )}

        {objectUrl && !isPdf && !isImage && (
          <div className="p-5 text-center">
            <p className="mb-1 fw-bold">This file type can&apos;t be previewed here.</p>
            <p className="text-muted">Download it to open in the right application.</p>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        {objectUrl && (
          <Button
            variant="outline-secondary"
            onClick={() => window.open(objectUrl, "_blank")}
            className="d-inline-flex align-items-center gap-2"
          >
            <ExternalLink size={16} /> Open in new tab
          </Button>
        )}
        <Button variant="outline-primary" onClick={download} className="d-inline-flex align-items-center gap-2">
          <Download size={16} /> Download
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AttachmentPreviewModal;
